import { useBlockProps, useInnerBlocksProps } from '@wordpress/block-editor';
import metadata from './block.json';
import { getWrapperClasses } from './styles';
import { getIconClasses } from '../../utils/helpers';

/**
 * The wrapper before Block spacing was serialized.
 *
 * blockGap used to be a declared support that nothing read, so a box with a
 * spacing value saved exactly the same markup as one without. Now that
 * styles.js writes `--brandy-info-box-gap`, those boxes would fail validation
 * on load; this reproduces the old wrapper so they stay valid and pick up the
 * property the next time they are edited.
 *
 * Frozen on purpose — it must keep emitting the old markup even as
 * getWrapperStyles() grows. Only the class list is shared, because that half
 * did not change.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} React style object.
 */
function getWrapperStylesV1(attributes) {
  const { iconSize, iconColor, iconBackgroundColor } = attributes;

  const styles = {
    '--brandy-info-box-icon-size': `${iconSize}px`,
  };

  if (iconColor) {
    styles['--brandy-info-box-icon-color'] = resolvePresetValueV1(iconColor);
  }

  if (iconBackgroundColor) {
    styles['--brandy-info-box-icon-bg'] = resolvePresetValueV1(iconBackgroundColor);
  }

  return styles;
}

/**
 * Preset resolution as it stood for v1, kept local for the same reason.
 *
 * @param {string} value Stored attribute value.
 * @return {string} CSS value.
 */
function resolvePresetValueV1(value) {
  if (typeof value !== 'string' || !value.startsWith('var:')) {
    return value;
  }

  return `var(--wp--${value.slice(4).replace(/\|/g, '--')})`;
}

const v1 = {
  attributes: metadata.attributes,
  supports: metadata.supports,
  save({ attributes }) {
    const { iconName, iconType } = attributes;

    const blockProps = useBlockProps.save({
      className: getWrapperClasses(attributes),
      style: getWrapperStylesV1(attributes),
    });

    const innerBlocksProps = useInnerBlocksProps.save({
      className: 'brandy-info-box__content',
    });

    const iconClasses = getIconClasses(iconName, iconType);

    return (
      <div {...blockProps}>
        {iconClasses && (
          <div className="brandy-info-box__media">
            <i className={iconClasses} aria-hidden="true" />
          </div>
        )}
        <div {...innerBlocksProps} />
      </div>
    );
  },
};

export default [v1];
